// /opt/isp_erp/frontend/src/components/warehouse/ActasRMA.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, ButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  TextField, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Alert,
  CircularProgress, Divider, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
  IconButton, Tooltip, TablePagination, InputAdornment
} from '@mui/material';
import { Description, History, PictureAsPdf, Block, Search, Refresh } from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';

const TIPOS = {
  ENTREGA_RMA: {
    nombre: 'Entrega a RMA', titulo: 'Acta de entrega a RMA', entrega: 'Quien entrega (Bodega)',
    recibe: 'Quien recibe (Departamento de RMA)', color: 'warning'
  },
  DEVOLUCION_BODEGA: {
    nombre: 'Devolución a bodega', titulo: 'Acta de devolución a bodega', entrega: 'Quien entrega (Departamento de RMA)',
    recibe: 'Quien recibe (Bodega)', color: 'success'
  }
};

const FALLAS = {
  FISICO: 'Daño físico', ELECTRONICO: 'Falla electrónica', FIRMWARE: 'Firmware',
  CONEXION: 'Conexión', OTRO: 'Otra falla'
};

const recorte = {
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 360
};

const pad = (n) => String(n).padStart(2, '0');
const fechaLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fechaHora = (v) => (v ? new Date(v).toLocaleString('es-EC', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
}) : '—');

const detalleError = (err, porDefecto) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return porDefecto;
};

const textoErrorBlob = async (err, porDefecto) => {
  try {
    const json = JSON.parse(await err.response.data.text());
    if (typeof json.detail === 'string') return json.detail;
  } catch (e) {
    /* sin detalle del servidor */
  }
  return porDefecto;
};

const bajarArchivo = (blob, nombre) => {
  const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nombre);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const ActasRMA = () => {
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [refrescar, setRefrescar] = useState(0);
  const [descargandoId, setDescargandoId] = useState(null);
  const [nombres, setNombres] = useState({ entrega: '', recibe: '' });

  // ---------- generar acta ----------
  const [tipoGen, setTipoGen] = useState(null);
  const [desde, setDesde] = useState(fechaLocal(new Date()));
  const [hasta, setHasta] = useState(fechaLocal(new Date()));
  const [candidatos, setCandidatos] = useState({ items: [], total: 0, hay_mas: false });
  const [cargandoCand, setCargandoCand] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [observaciones, setObservaciones] = useState('');
  const [generando, setGenerando] = useState(false);
  const [errorGen, setErrorGen] = useState('');

  // ---------- historial ----------
  const [histAbierto, setHistAbierto] = useState(false);
  const [histTipo, setHistTipo] = useState('');
  const [histTexto, setHistTexto] = useState('');
  const [histBusqueda, setHistBusqueda] = useState('');
  const [histDesde, setHistDesde] = useState('');
  const [histHasta, setHistHasta] = useState('');
  const [histAnuladas, setHistAnuladas] = useState(false);
  const [histPage, setHistPage] = useState(0);
  const [histRows, setHistRows] = useState(10);
  const [hist, setHist] = useState({ items: [], total: 0 });
  const [cargandoHist, setCargandoHist] = useState(false);
  const [errorHist, setErrorHist] = useState('');

  const [anulando, setAnulando] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [errorAnular, setErrorAnular] = useState('');

  const avisar = (txt) => {
    setMensaje(txt);
    setTimeout(() => setMensaje(''), 6000);
  };

  // ---------- candidatos ----------
  const cargarCandidatos = useCallback(async () => {
    if (!tipoGen) return;
    setCargandoCand(true);
    setErrorGen('');
    try {
      const params = { tipo: tipoGen };
      if (desde) params.fecha_desde = desde;
      if (hasta) params.fecha_hasta = hasta;
      const r = await warehouseAPI.candidatosActa(params);
      setCandidatos(r.data);
      setSeleccionados(new Set((r.data.items || []).map((i) => i.movement_id)));
    } catch (err) {
      setCandidatos({ items: [], total: 0, hay_mas: false });
      setSeleccionados(new Set());
      setErrorGen(detalleError(err, 'No se pudieron cargar los equipos'));
    } finally {
      setCargandoCand(false);
    }
  }, [tipoGen, desde, hasta]);

  useEffect(() => {
    cargarCandidatos();
  }, [cargarCandidatos]);

  const abrirGenerar = (tipo) => {
    const hoy = fechaLocal(new Date());
    setDesde(hoy);
    setHasta(hoy);
    setObservaciones('');
    setErrorGen('');
    setSeleccionados(new Set());
    setCandidatos({ items: [], total: 0, hay_mas: false });
    setTipoGen(tipo);
  };

  const rangoRapido = (tipo) => {
    const hoy = new Date();
    if (tipo === 'hoy') {
      setDesde(fechaLocal(hoy));
      setHasta(fechaLocal(hoy));
    } else if (tipo === 'ayer') {
      const a = new Date(hoy);
      a.setDate(a.getDate() - 1);
      setDesde(fechaLocal(a));
      setHasta(fechaLocal(a));
    } else if (tipo === 'semana') {
      const a = new Date(hoy);
      a.setDate(a.getDate() - 6);
      setDesde(fechaLocal(a));
      setHasta(fechaLocal(hoy));
    } else {
      setDesde(`${fechaLocal(hoy).slice(0, 8)}01`);
      setHasta(fechaLocal(hoy));
    }
  };

  const todosMarcados = candidatos.items.length > 0 && seleccionados.size === candidatos.items.length;
  const algunosMarcados = seleccionados.size > 0 && !todosMarcados;

  const alternarTodos = () => {
    if (todosMarcados) setSeleccionados(new Set());
    else setSeleccionados(new Set(candidatos.items.map((i) => i.movement_id)));
  };

  const alternar = (id) => {
    const nuevo = new Set(seleccionados);
    if (nuevo.has(id)) nuevo.delete(id);
    else nuevo.add(id);
    setSeleccionados(nuevo);
  };

  // ---------- descarga ----------
  const descargarActa = async (acta) => {
    setDescargandoId(acta.id);
    try {
      const r = await warehouseAPI.descargarActaPDF(acta.id);
      bajarArchivo(r.data, `acta_${acta.numero}.pdf`);
    } catch (err) {
      setError(await textoErrorBlob(err, 'No se pudo descargar el PDF. Puedes reimprimirlo desde el historial.'));
    } finally {
      setDescargandoId(null);
    }
  };

  // ---------- generar ----------
  const generar = async () => {
    if (seleccionados.size === 0) {
      setErrorGen('Marca al menos un equipo para el acta');
      return;
    }
    if (nombres.entrega.trim().length < 2 || nombres.recibe.trim().length < 2) {
      setErrorGen('Escribe el nombre de quien entrega y de quien recibe');
      return;
    }
    setGenerando(true);
    setErrorGen('');
    try {
      const r = await warehouseAPI.crearActa({
        tipo: tipoGen,
        movimiento_ids: Array.from(seleccionados),
        entrega_nombre: nombres.entrega.trim(),
        recibe_nombre: nombres.recibe.trim(),
        observaciones: observaciones.trim() || null,
        fecha_desde: desde || null,
        fecha_hasta: hasta || null
      });
      const acta = r.data.acta;
      setTipoGen(null);
      avisar(`${r.data.message}. Se está descargando el PDF.`);
      setRefrescar((n) => n + 1);
      await descargarActa(acta);
    } catch (err) {
      setErrorGen(detalleError(err, 'No se pudo generar el acta'));
    } finally {
      setGenerando(false);
    }
  };

  // ---------- historial ----------
  useEffect(() => {
    const t = setTimeout(() => {
      setHistBusqueda(histTexto.trim());
      setHistPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [histTexto]);

  const cargarHistorial = useCallback(async () => {
    if (!histAbierto) return;
    setCargandoHist(true);
    setErrorHist('');
    try {
      const params = { page: histPage + 1, page_size: histRows, incluir_anuladas: histAnuladas };
      if (histTipo) params.tipo = histTipo;
      if (histBusqueda) params.q = histBusqueda;
      if (histDesde) params.fecha_desde = histDesde;
      if (histHasta) params.fecha_hasta = histHasta;
      const r = await warehouseAPI.listarActas(params);
      setHist(r.data);
    } catch (err) {
      setHist({ items: [], total: 0 });
      setErrorHist(detalleError(err, 'No se pudo cargar el historial'));
    } finally {
      setCargandoHist(false);
    }
  }, [histAbierto, histTipo, histBusqueda, histDesde, histHasta, histAnuladas, histPage, histRows, refrescar]);

  useEffect(() => {
    cargarHistorial();
  }, [cargarHistorial]);

  const confirmarAnular = async () => {
    if (motivo.trim().length < 3) {
      setErrorAnular('Indica el motivo de la anulación');
      return;
    }
    try {
      await warehouseAPI.anularActa(anulando.id, { motivo: motivo.trim() });
      setAnulando(null);
      setMotivo('');
      avisar('Acta anulada. Sus equipos quedaron libres para otra acta.');
      setRefrescar((n) => n + 1);
    } catch (err) {
      setErrorAnular(detalleError(err, 'No se pudo anular el acta'));
    }
  };

  const cfg = tipoGen ? TIPOS[tipoGen] : null;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, bgcolor: '#f3f8ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">Actas de RMA</Typography>
          <Typography variant="body2" color="text.secondary">
            Documentos numerados, con espacio de firma, que respaldan lo que se entrega a RMA y lo que RMA devuelve.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={<Description />} onClick={() => abrirGenerar('ENTREGA_RMA')}>
            Acta de entrega a RMA
          </Button>
          <Button variant="contained" color="success" startIcon={<Description />} onClick={() => abrirGenerar('DEVOLUCION_BODEGA')}>
            Acta de devolución a bodega
          </Button>
          <Button variant="outlined" startIcon={<History />} onClick={() => setHistAbierto(true)}>
            Historial de actas
          </Button>
        </Box>
      </Paper>
      {mensaje && <Alert severity="success" sx={{ mb: 2 }}>{mensaje}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ---------- Generar acta ---------- */}
      <Dialog open={!!tipoGen} onClose={() => !generando && setTipoGen(null)} maxWidth="lg" fullWidth>
        <DialogTitle>{cfg ? cfg.titulo : ''}</DialogTitle>
        <DialogContent dividers>
          {errorGen && <Alert severity="error" sx={{ mb: 2 }}>{errorGen}</Alert>}

          <Typography variant="subtitle2" color="primary" gutterBottom>1. Período de los movimientos</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={6} md={2.5}>
              <TextField fullWidth size="small" type="date" label="Desde" value={desde} onChange={(e) => setDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <TextField fullWidth size="small" type="date" label="Hasta" value={hasta} onChange={(e) => setHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={5}>
              <ButtonGroup size="small" variant="outlined">
                <Button onClick={() => rangoRapido('hoy')}>Hoy</Button>
                <Button onClick={() => rangoRapido('ayer')}>Ayer</Button>
                <Button onClick={() => rangoRapido('semana')}>7 días</Button>
                <Button onClick={() => rangoRapido('mes')}>Este mes</Button>
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth size="small" variant="outlined" startIcon={<Refresh />} onClick={cargarCandidatos}>
                Buscar
              </Button>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" color="primary" gutterBottom>2. Equipos que irán en el acta</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Chip label={`Disponibles: ${candidatos.total}`} />
            <Chip color="primary" label={`Seleccionados: ${seleccionados.size}`} />
          </Box>
          {candidatos.hay_mas && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Hay más equipos que los mostrados (máximo 500). Reduce el rango de fechas para verlos todos.
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, mb: 3 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ bgcolor: '#f5f5f5' }}>
                    <Checkbox
                      checked={todosMarcados}
                      indeterminate={algunosMarcados}
                      onChange={alternarTodos}
                      disabled={candidatos.items.length === 0}
                    />
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Equipo</TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>
                    {tipoGen === 'ENTREGA_RMA' ? 'Falla y diagnóstico' : 'Observaciones de la reparación'}
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Fecha y hora</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cargandoCand ? (
                  <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={22} /></TableCell></TableRow>
                ) : candidatos.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No hay equipos disponibles en este período. Los que ya están en un acta vigente no aparecen.
                    </TableCell>
                  </TableRow>
                ) : (
                  candidatos.items.map((i) => (
                    <TableRow key={i.movement_id} hover onClick={() => alternar(i.movement_id)} sx={{ cursor: 'pointer' }}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={seleccionados.has(i.movement_id)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{i.sn}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {i.modelo || '—'}{i.tipo ? ` · ${i.tipo}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {tipoGen === 'ENTREGA_RMA' ? (
                          <>
                            <Chip size="small" variant="outlined" label={FALLAS[i.tipo_falla] || 'Sin tipo'} sx={{ mb: 0.5 }} />
                            <Typography variant="body2" sx={recorte}>{i.diagnostico || '—'}</Typography>
                          </>
                        ) : (
                          <Typography variant="body2" sx={recorte}>{i.observaciones || '—'}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fechaHora(i.fecha_movimiento)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" color="primary" gutterBottom>3. Firmas y observaciones</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" label={`${cfg ? cfg.entrega : ''} *`} value={nombres.entrega}
                onChange={(e) => setNombres({ ...nombres, entrega: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" label={`${cfg ? cfg.recibe : ''} *`} value={nombres.recibe}
                onChange={(e) => setNombres({ ...nombres, recibe: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Observaciones del acta (opcional)" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTipoGen(null)} disabled={generando}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={generando ? <CircularProgress size={16} /> : <PictureAsPdf />}
            onClick={generar}
            disabled={generando || seleccionados.size === 0}
          >
            {generando ? 'Generando...' : `Generar acta y descargar PDF (${seleccionados.size})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Historial ---------- */}
      <Dialog open={histAbierto} onClose={() => setHistAbierto(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Historial de actas de RMA</DialogTitle>
        <DialogContent dividers>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth size="small" label="Buscar por número, nombre o SN" value={histTexto}
                  onChange={(e) => setHistTexto(e.target.value)}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de acta</InputLabel>
                  <Select label="Tipo de acta" value={histTipo} onChange={(e) => { setHistTipo(e.target.value); setHistPage(0); }}>
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="ENTREGA_RMA">Entrega a RMA</MenuItem>
                    <MenuItem value="DEVOLUCION_BODEGA">Devolución a bodega</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2.5}>
                <TextField fullWidth size="small" type="date" label="Desde" value={histDesde} onChange={(e) => { setHistDesde(e.target.value); setHistPage(0); }} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} md={2.5}>
                <TextField fullWidth size="small" type="date" label="Hasta" value={histHasta} onChange={(e) => { setHistHasta(e.target.value); setHistPage(0); }} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={histAnuladas} onChange={(e) => { setHistAnuladas(e.target.checked); setHistPage(0); }} />}
                  label="Incluir actas anuladas"
                />
              </Grid>
            </Grid>
          </Paper>
          {errorHist && <Alert severity="error" sx={{ mb: 2 }}>{errorHist}</Alert>}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Acta</TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Emisión</TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Entrega → Recibe</TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}>Equipos</TableCell>
                  <TableCell align="center" sx={{ bgcolor: '#f5f5f5' }}>Estado y acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cargandoHist ? (
                  <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={22} /></TableCell></TableRow>
                ) : hist.items.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center">No hay actas con esos filtros</TableCell></TableRow>
                ) : (
                  hist.items.map((a) => (
                    <TableRow key={a.id} hover sx={{ opacity: a.anulada ? 0.55 : 1 }}>
                      <TableCell sx={{ minWidth: 190 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ textDecoration: a.anulada ? 'line-through' : 'none' }}>
                          {a.numero}
                        </Typography>
                        <Chip size="small" variant="outlined" color={TIPOS[a.tipo] ? TIPOS[a.tipo].color : 'default'} label={a.tipo_nombre} sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Typography variant="body2">{fechaHora(a.fecha_acta)}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{a.usuario || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2">{a.entrega_nombre || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">→ {a.recibe_nombre || '—'}</Typography>
                      </TableCell>
                      <TableCell align="right"><strong>{a.cantidad}</strong></TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {a.anulada ? (
                          <Tooltip title={`Anulada por ${a.anulada_por || '—'}: ${a.motivo_anulacion || ''}`}>
                            <Chip size="small" label="Anulada" sx={{ mr: 0.5 }} />
                          </Tooltip>
                        ) : (
                          <Chip size="small" color="success" label="Vigente" sx={{ mr: 0.5 }} />
                        )}
                        <Tooltip title="Descargar / reimprimir PDF">
                          <span>
                            <IconButton size="small" onClick={() => descargarActa(a)} disabled={descargandoId === a.id}>
                              {descargandoId === a.id ? <CircularProgress size={16} /> : <PictureAsPdf fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {!a.anulada && (
                          <Tooltip title="Anular acta">
                            <IconButton size="small" color="error" onClick={() => { setAnulando(a); setMotivo(''); setErrorAnular(''); }}>
                              <Block fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={hist.total || 0}
            page={histPage}
            rowsPerPage={histRows}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            onPageChange={(e, nueva) => setHistPage(nueva)}
            onRowsPerPageChange={(e) => { setHistRows(parseInt(e.target.value, 10)); setHistPage(0); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistAbierto(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!anulando} onClose={() => setAnulando(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Anular acta {anulando?.numero}</DialogTitle>
        <DialogContent>
          {errorAnular && <Alert severity="error" sx={{ mb: 2 }}>{errorAnular}</Alert>}
          <Alert severity="warning" sx={{ mb: 2 }}>
            El acta queda en el historial como anulada y su PDF mostrará el aviso. Los {anulando?.cantidad} equipos quedan
            libres para incluirse en otra acta. Esto no deshace movimientos de bodega ni cambia el estado de los equipos.
          </Alert>
          <TextField fullWidth multiline rows={2} label="Motivo de la anulación *" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnulando(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmarAnular}>Anular acta</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActasRMA;
